import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import { generationsAPI, uploadAPI } from '../services/api';
import Lightbox from '../components/Lightbox';
import './Generate.css';

// Clean up error messages for display
const getCleanErrorMessage = (error) => {
  const message = error?.response?.data?.detail || error?.message || String(error);

  if (message.includes('504') || message.includes('Gateway time-out')) {
    return 'The service was temporarily busy. Please try again.';
  }
  if (message.includes('502') || message.includes('503')) {
    return 'The service is experiencing high demand. Please try again.';
  }
  if (message.includes('timeout') || message.includes('timed out')) {
    return 'Request timed out. Please try again.';
  }
  if (message.includes('<!DOCTYPE') || message.includes('<html')) {
    return 'Service temporarily unavailable. Please try again.';
  }
  if (message.length > 100) {
    return 'Generation failed. Please try again.';
  }

  return message;
};

function Generate() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, refreshUser } = useAuth();

  const [step, setStep] = useState(1);
  const [options, setOptions] = useState(null);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);

  // Form state
  const [generationType, setGenerationType] = useState(
    searchParams.get('type') || ''
  );
  const [materialType, setMaterialType] = useState('');
  const [color, setColor] = useState('');
  const [customInstructions, setCustomInstructions] = useState('');
  const [spaceImage, setSpaceImage] = useState(null);
  const [spaceImageUrl, setSpaceImageUrl] = useState('');
  const [referenceImage, setReferenceImage] = useState(null);
  const [referenceImageUrl, setReferenceImageUrl] = useState('');
  const [result, setResult] = useState(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const response = await generationsAPI.getOptions();
        setOptions(response.data);
      } catch (error) {
        console.error('Failed to fetch options:', error);
        toast.error('Failed to load options');
      }
    };
    fetchOptions();
  }, []);

  const onSpaceImageDrop = useCallback((acceptedFiles) => {
    const file = acceptedFiles[0];
    if (file) {
      setSpaceImage(file);
      setSpaceImageUrl(URL.createObjectURL(file));
    }
  }, []);

  const handleDownload = async (generationId) => {
    try {
      const token = localStorage.getItem('token');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      const response = await fetch(`${API_URL}/generations/${generationId}/download`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) throw new Error('Download failed');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `redoit-design-${generationId}.jpg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download failed:', error);
    }
  };

  const onReferenceImageDrop = useCallback((acceptedFiles) => {
    const file = acceptedFiles[0];
    if (file) {
      setReferenceImage(file);
      setReferenceImageUrl(URL.createObjectURL(file));
    }
  }, []);

  const {
    getRootProps: getSpaceRootProps,
    getInputProps: getSpaceInputProps,
    isDragActive: isSpaceDragActive,
  } = useDropzone({
    onDrop: onSpaceImageDrop,
    accept: { 'image/*': ['.png', '.jpg', '.jpeg', '.webp'] },
    maxFiles: 1,
    maxSize: 10 * 1024 * 1024,
  });

  const {
    getRootProps: getReferenceRootProps,
    getInputProps: getReferenceInputProps,
    isDragActive: isReferenceDragActive,
  } = useDropzone({
    onDrop: onReferenceImageDrop,
    accept: { 'image/*': ['.png', '.jpg', '.jpeg', '.webp'] },
    maxFiles: 1,
    maxSize: 10 * 1024 * 1024,
  });

  const selectedTypeData = options?.generation_types?.find(
    (t) => t.value === generationType
  );

  const handleGenerate = async () => {
    if (!spaceImage) {
      toast.error('Please upload an image of your space');
      return;
    }

    if (!generationType) {
      toast.error('Please select a generation type');
      return;
    }

    if (user?.credits < 1) {
      toast.error('Insufficient credits. Please purchase more.');
      navigate('/pricing');
      return;
    }

    setGenerating(true);
    try {
      // Upload space image
      const spaceUploadResponse = await uploadAPI.uploadImage(spaceImage);
      const uploadedSpaceUrl = spaceUploadResponse.data.url;

      // Upload reference image if provided
      let uploadedReferenceUrl = null;
      if (referenceImage) {
        const refUploadResponse = await uploadAPI.uploadReference(referenceImage);
        uploadedReferenceUrl = refUploadResponse.data.url;
      }

      // Create generation
      const generationData = {
        generation_type: generationType,
        input_image_url: uploadedSpaceUrl,
        material_type: materialType || null,
        color: color || null,
        reference_image_url: uploadedReferenceUrl,
        custom_instructions: customInstructions || null,
      };

      const response = await generationsAPI.create(generationData);
      setResult(response.data);
      setStep(4);
      await refreshUser();
      toast.success('Design generated successfully!');
    } catch (error) {
      console.error('Generation failed:', error);
      toast.error(getCleanErrorMessage(error));
    } finally {
      setGenerating(false);
    }
  };

  const resetForm = () => {
    setStep(1);
    setGenerationType('');
    setMaterialType('');
    setColor('');
    setCustomInstructions('');
    setSpaceImage(null);
    setSpaceImageUrl('');
    setReferenceImage(null);
    setReferenceImageUrl('');
    setResult(null);
  };

  if (!options) {
    return (
      <div className="page">
        <div className="container">
          <div className="loading-state">
            <div className="spinner" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page generate-page">
      <div className="container">
        <div className="generate-header">
          <h1 className="page-title">Create New Design</h1>
          <div className="credits-display">
            <span className="credits-icon">C</span>
            <span>{user?.credits?.toFixed(0) || 0} credits</span>
          </div>
        </div>

        {/* Progress Steps */}
        <div className="progress-steps">
          <div className={`progress-step ${step >= 1 ? 'active' : ''}`}>
            <span className="step-num">1</span>
            <span className="step-label">Upload</span>
          </div>
          <div className={`progress-step ${step >= 2 ? 'active' : ''}`}>
            <span className="step-num">2</span>
            <span className="step-label">Choose Style</span>
          </div>
          <div className={`progress-step ${step >= 3 ? 'active' : ''}`}>
            <span className="step-num">3</span>
            <span className="step-label">Customize</span>
          </div>
          <div className={`progress-step ${step >= 4 ? 'active' : ''}`}>
            <span className="step-num">4</span>
            <span className="step-label">Result</span>
          </div>
        </div>

        {/* Step 1: Upload Image */}
        {step === 1 && (
          <div className="generate-step">
            <h2>Upload Your Space</h2>
            <p className="step-description">
              Take a photo of the room or area you want to redesign
            </p>

            <div
              {...getSpaceRootProps()}
              className={`dropzone ${isSpaceDragActive ? 'active' : ''} ${
                spaceImageUrl ? 'has-image' : ''
              }`}
            >
              <input {...getSpaceInputProps()} />
              {spaceImageUrl ? (
                <div className="dropzone-preview">
                  <img src={spaceImageUrl} alt="Your space" />
                  <button
                    className="remove-image"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSpaceImage(null);
                      setSpaceImageUrl('');
                    }}
                  >
                    ×
                  </button>
                </div>
              ) : (
                <div className="dropzone-content">
                  <span className="dropzone-icon">📷</span>
                  <p className="dropzone-text">
                    Drag & drop your image here, or click to select
                  </p>
                  <span className="dropzone-hint">
                    PNG, JPG, WEBP up to 10MB
                  </span>
                </div>
              )}
            </div>

            <div className="step-actions">
              <button
                className="btn btn-primary"
                disabled={!spaceImageUrl}
                onClick={() => setStep(2)}
              >
                Continue
              </button>
            </div>
          </div>
        )}

        {/* Step 2: Choose Type */}
        {step === 2 && (
          <div className="generate-step">
            <h2>What Would You Like to Change?</h2>
            <p className="step-description">
              Select the type of transformation you want
            </p>

            <div className="type-grid">
              {options.generation_types.map((type) => (
                <button
                  key={type.value}
                  className={`type-card card ${
                    generationType === type.value ? 'selected' : ''
                  }`}
                  onClick={() => {
                    setGenerationType(type.value);
                    setMaterialType('');
                  }}
                >
                  <span className="type-label">{type.label}</span>
                  <span className="type-description">{type.description}</span>
                </button>
              ))}
            </div>

            <div className="step-actions">
              <button className="btn btn-secondary" onClick={() => setStep(1)}>
                Back
              </button>
              <button
                className="btn btn-primary"
                disabled={!generationType}
                onClick={() => setStep(3)}
              >
                Continue
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Customize */}
        {step === 3 && (
          <div className="generate-step">
            <h2>Customize Your Design</h2>
            <p className="step-description">
              Choose materials, colors, or upload a reference image
            </p>

            <div className="customize-grid">
              {/* Material Selection */}
              {selectedTypeData?.materials && (
                <div className="customize-section">
                  <label className="label">Material</label>
                  <div className="material-grid">
                    {selectedTypeData.materials.map((mat) => {
                      const matData = options.material_types.find(
                        (m) => m.value === mat
                      );
                      return (
                        <button
                          key={mat}
                          className={`material-card ${
                            materialType === mat ? 'selected' : ''
                          }`}
                          onClick={() => setMaterialType(mat)}
                        >
                          <span className="material-name">
                            {matData?.label || mat}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Color Selection (for paint) */}
              {selectedTypeData?.requires_color && (
                <div className="customize-section">
                  <label className="label">Color</label>
                  <input
                    type="text"
                    className="input"
                    placeholder="e.g., Navy blue, Warm beige, Forest green"
                    value={color}
                    onChange={(e) => setColor(e.target.value)}
                  />
                </div>
              )}

              {/* Finish Selection (for paint) */}
              {selectedTypeData?.finishes && (
                <div className="customize-section">
                  <label className="label">Finish</label>
                  <div className="material-grid">
                    {selectedTypeData.finishes.map((finish) => (
                      <button
                        key={finish}
                        className={`material-card ${
                          materialType === finish ? 'selected' : ''
                        }`}
                        onClick={() => setMaterialType(finish)}
                      >
                        <span className="material-name">
                          {finish.replace('_', ' ')}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Reference Image */}
              <div className="customize-section">
                <label className="label">
                  Reference Image (Optional)
                  <span className="label-hint">
                    Upload a sample of the material/style you want
                  </span>
                </label>
                <div
                  {...getReferenceRootProps()}
                  className={`dropzone small ${
                    isReferenceDragActive ? 'active' : ''
                  } ${referenceImageUrl ? 'has-image' : ''}`}
                >
                  <input {...getReferenceInputProps()} />
                  {referenceImageUrl ? (
                    <div className="dropzone-preview">
                      <img src={referenceImageUrl} alt="Reference" />
                      <button
                        className="remove-image"
                        onClick={(e) => {
                          e.stopPropagation();
                          setReferenceImage(null);
                          setReferenceImageUrl('');
                        }}
                      >
                        ×
                      </button>
                    </div>
                  ) : (
                    <div className="dropzone-content">
                      <span className="dropzone-icon">🖼️</span>
                      <p className="dropzone-text">
                        Upload material sample or style reference
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Custom Instructions */}
              <div className="customize-section">
                <label className="label">
                  Additional Instructions (Optional)
                </label>
                <textarea
                  className="input"
                  rows={3}
                  placeholder="Any specific details or preferences..."
                  value={customInstructions}
                  onChange={(e) => setCustomInstructions(e.target.value)}
                />
              </div>
            </div>

            {/* Preview */}
            <div className="preview-section">
              <h3>Preview</h3>
              <div className="preview-images">
                <div className="preview-image">
                  <span className="preview-label">Your Space</span>
                  <img src={spaceImageUrl} alt="Your space" />
                </div>
                {referenceImageUrl && (
                  <div className="preview-image">
                    <span className="preview-label">Reference</span>
                    <img src={referenceImageUrl} alt="Reference" />
                  </div>
                )}
              </div>
            </div>

            <div className="generation-cost">
              <span>Cost: 1 credit</span>
              <span className="cost-note">
                Current balance: {user?.credits?.toFixed(0) || 0} credits
              </span>
            </div>

            <div className="step-actions">
              <button className="btn btn-secondary" onClick={() => setStep(2)}>
                Back
              </button>
              <button
                className="btn btn-primary"
                disabled={generating}
                onClick={handleGenerate}
              >
                {generating ? (
                  <>
                    <span className="spinner small" /> Generating...
                  </>
                ) : (
                  'Generate Design'
                )}
              </button>
            </div>
          </div>
        )}

        {/* Step 4: Result */}
        {step === 4 && result && (
          <div className="generate-step result-step">
            <h2>Your New Design</h2>
            <p className="step-description">Click on images to view full size</p>

            <div className="result-comparison">
              <div
                className="result-image clickable"
                onClick={() => {
                  setLightboxIndex(0);
                  setLightboxOpen(true);
                }}
              >
                <span className="result-label">Before</span>
                <img src={spaceImageUrl} alt="Before" />
                <div className="image-overlay">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" />
                  </svg>
                </div>
              </div>
              <div
                className="result-image clickable"
                onClick={() => {
                  setLightboxIndex(1);
                  setLightboxOpen(true);
                }}
              >
                <span className="result-label">After</span>
                <img src={result.output_image_url} alt="After" />
                <div className="image-overlay">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" />
                  </svg>
                </div>
              </div>
            </div>

            <div className="result-details card">
              <div className="detail-row">
                <span className="detail-label">Type:</span>
                <span className="detail-value">
                  {result.generation_type.replace('_', ' ')}
                </span>
              </div>
              {result.material_type && (
                <div className="detail-row">
                  <span className="detail-label">Material:</span>
                  <span className="detail-value">
                    {result.material_type.replace('_', ' ')}
                  </span>
                </div>
              )}
              {result.color && (
                <div className="detail-row">
                  <span className="detail-label">Color:</span>
                  <span className="detail-value">{result.color}</span>
                </div>
              )}
            </div>

            <div className="result-actions">
              <button
                className="btn btn-secondary"
                onClick={() => handleDownload(result.id)}
              >
                Download Image
              </button>
              <button className="btn btn-primary" onClick={resetForm}>
                Create Another Design
              </button>
            </div>
          </div>
        )}

        {/* Lightbox */}
        {lightboxOpen && result && (
          <Lightbox
            images={[
              { url: spaceImageUrl, label: 'Before', alt: 'Original room' },
              { url: result.output_image_url, label: 'After', alt: 'AI generated design' },
            ]}
            initialIndex={lightboxIndex}
            onClose={() => setLightboxOpen(false)}
          />
        )}
      </div>
    </div>
  );
}

export default Generate;
